import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase, edgeFunctionErrorBody } from '@/lib/supabase';
import { fmtDataHoraLocal, fmtMoeda } from '@/pages/plan/shared';

/**
 * Desbloqueio em confiança (P-46): quem pagou o boleto e ainda espera a
 * compensação anexa o comprovante e libera o acesso por um prazo curto.
 *
 * Toda regra mora na Edge Function `liberar-acesso` — fatura em aberto, paga
 * por boleto, uma vez por fatura. A tela só mostra o que a função responde e
 * nunca grava a liberação por conta própria (a tabela nem aceita).
 */

const BUCKET = 'comprovantes-pagamento';
const TAMANHO_MAXIMO = 10 * 1024 * 1024;
const TIPOS_ACEITOS = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

interface Situacao {
  accountId: string;
  horas: number;
  liberacaoVigente: { expiraEm: string; origem: string } | null;
  fatura: { id: string; numero: string | null; valor: number; urlBoleto: string | null } | null;
  elegivel: boolean;
  motivo: string | null;
}

async function consultar(): Promise<Situacao | null> {
  const { data, error } = await supabase.functions.invoke('liberar-acesso', {
    body: { acao: 'consultar' },
  });
  if (error || !data) return null;
  return data as Situacao;
}

export default function DesbloqueioConfianca() {
  const [situacao, setSituacao] = useState<Situacao | null>(null);
  const [aberto, setAberto] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [confirmo, setConfirmo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    consultar()
      .then((s) => {
        if (ativo) setSituacao(s);
      })
      .catch(() => {
        /* sem resposta, a tela segue sem o desbloqueio — o status já aparece acima */
      });
    return () => {
      ativo = false;
    };
  }, []);

  if (!situacao) return null;

  if (situacao.liberacaoVigente) {
    return (
      <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-lock-unlock-line text-green-600 text-xs"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-green-800">
              Acesso liberado até {fmtDataHoraLocal(situacao.liberacaoVigente.expiraEm)}
            </p>
            <p className="text-xs text-green-700 mt-0.5">
              Assim que o banco confirmar o pagamento, a assinatura é ativada e o acesso segue
              normalmente. Se o pagamento não for confirmado até lá, o acesso volta a ser bloqueado.
            </p>
          </div>
        </div>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          Acessar o sistema
        </Link>
      </div>
    );
  }

  if (situacao.motivo === 'ja_utilizado') {
    return (
      <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-background-100 border border-background-200 flex items-start gap-3">
        <i className="ri-information-line text-foreground-500 mt-0.5"></i>
        <p className="text-xs text-foreground-600">
          O desbloqueio em confiança desta fatura já foi utilizado. Se você já pagou e o acesso
          continua bloqueado, fale com o suporte.
        </p>
      </div>
    );
  }

  if (!situacao.elegivel || !situacao.fatura) return null;

  const fatura = situacao.fatura;

  const escolherArquivo = (selecionado: File | null) => {
    setErro(null);
    if (!selecionado) {
      setArquivo(null);
      return;
    }
    if (!TIPOS_ACEITOS.includes(selecionado.type)) {
      setArquivo(null);
      setErro('Envie o comprovante em PDF, JPG, PNG ou WEBP.');
      return;
    }
    if (selecionado.size > TAMANHO_MAXIMO) {
      setArquivo(null);
      setErro('O comprovante deve ter no máximo 10 MB.');
      return;
    }
    setArquivo(selecionado);
  };

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!arquivo || !confirmo) return;
    setEnviando(true);
    setErro(null);

    try {
      // Dois segmentos, `<conta>/<arquivo>`: é o formato que a policy do bucket
      // e a função conferem. O nome original não entra — acento e espaço viram
      // chave inválida no storage.
      const extensao = arquivo.name.split('.').pop()?.toLowerCase() || 'pdf';
      const caminho = `${situacao.accountId}/${fatura.id}-${Date.now()}.${extensao}`;

      const { error: erroUpload } = await supabase.storage
        .from(BUCKET)
        .upload(caminho, arquivo, { contentType: arquivo.type, upsert: false });
      if (erroUpload) {
        setErro('Não foi possível enviar o comprovante. Tente novamente.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('liberar-acesso', {
        body: { acao: 'solicitar', comprovante: caminho, confirmoPagamento: true },
      });
      if (error || !data?.liberado) {
        const corpo = error ? await edgeFunctionErrorBody(error) : null;
        setErro(corpo?.error || 'Não foi possível liberar o acesso. Tente novamente.');
        return;
      }

      setSituacao({
        ...situacao,
        liberacaoVigente: { expiraEm: data.expiraEm as string, origem: 'cliente' },
        elegivel: false,
      });
    } catch {
      setErro('Não foi possível liberar o acesso. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mx-5 mt-4 px-4 py-4 rounded-lg border border-primary-200 bg-primary-50/40">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground-900">Já pagou o boleto?</p>
          <p className="text-xs text-foreground-600 mt-0.5">
            Envie o comprovante e libere o acesso por {situacao.horas} horas enquanto o banco
            confirma o pagamento. Disponível uma vez por fatura.
          </p>
        </div>
        {!aberto && (
          <button
            type="button"
            onClick={() => setAberto(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-lock-unlock-line"></i>
            Já paguei o boleto
          </button>
        )}
      </div>

      {aberto && (
        <form onSubmit={enviar} className="mt-4 flex flex-col gap-3">
          <div>
            <label
              htmlFor="comprovante-boleto"
              className="block text-xs font-medium text-foreground-700 mb-1"
            >
              Comprovante de pagamento (PDF ou imagem, até 10 MB)
            </label>
            <input
              id="comprovante-boleto"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => escolherArquivo(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-foreground-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-background-300 file:bg-background-50 file:text-sm file:font-medium hover:file:bg-background-100"
            />
          </div>

          <label className="flex items-start gap-2 text-xs text-foreground-700 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmo}
              onChange={(e) => setConfirmo(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Declaro que paguei o boleto
              {fatura.numero ? ` da fatura ${fatura.numero}` : ''} no valor de{' '}
              {fmtMoeda(fatura.valor / 100)}. Sei que, se o pagamento não for confirmado em{' '}
              {situacao.horas} horas, o acesso volta a ser bloqueado.
            </span>
          </label>

          {erro && (
            <p role="alert" className="text-xs text-red-700">
              {erro}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={!arquivo || !confirmo || enviando}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {enviando ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Liberando...
                </>
              ) : (
                <>
                  <i className="ri-lock-unlock-line"></i>
                  Liberar acesso por {situacao.horas} horas
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setAberto(false);
                setErro(null);
              }}
              disabled={enviando}
              className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
